import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  CreditCard as PaymentIcon,
  AccountBalance as BankIcon,
  Money as CashIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';

const PaymentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch payment details
  const { data: payment, isLoading, error } = useQuery({
    queryKey: ['payment', id],
    queryFn: async () => {
      const response = await apiClient.get(`/financial/payments/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
    } catch {
      return 'Invalid date';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'card':
      case 'credit_card':
      case 'debit_card':
        return <PaymentIcon />;
      case 'bank_transfer':
        return <BankIcon />;
      case 'cash':
        return <CashIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load payment details. Please try again later.
        </Alert>
        <Button onClick={() => navigate('/financial/payments')} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to Payments
        </Button>
      </Box>
    );
  }

  // For now, show basic payment data since we don't have the detail endpoint
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/financial/payments')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Payment #{payment?.payment_id || id?.slice(-6)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Received {formatDate(payment?.payment_date || payment?.created_at)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<ReceiptIcon />} variant="outlined">
            View Invoice
          </Button>
          <Button startIcon={<PrintIcon />} variant="outlined">
            Print Receipt
          </Button>
          <Button startIcon={<EmailIcon />} variant="outlined">
            Email Receipt
          </Button>
          <Button startIcon={<DownloadIcon />} variant="outlined">
            Download
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment ID
                </Typography>
                <Typography variant="body1">
                  {payment?.payment_id || `PAY-${id?.slice(-6)}`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={payment?.status || 'Completed'}
                  color={getStatusColor(payment?.status || 'completed')}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Date
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(payment?.payment_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Method
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getPaymentMethodIcon(payment?.payment_method)}
                  <Typography variant="body1">
                    {payment?.payment_method || 'Unknown Method'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1">
                  {payment?.client_name || 'Unknown Client'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(payment?.amount)}
                </Typography>
              </Grid>
              {payment?.fee_amount && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Processing Fee
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(payment?.fee_amount)}
                  </Typography>
                </Grid>
              )}
              {payment?.fee_amount && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Net Amount
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency((payment?.amount || 0) - (payment?.fee_amount || 0))}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {payment?.payment_note && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Payment Note
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1">
                    {payment.payment_note}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Related Invoice
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {payment?.invoice_id ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Invoice #:</Typography>
                  <Typography variant="body1">
                    {payment.invoice_number || payment.invoice_id.slice(-6)}
                  </Typography>
                </Box>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => navigate(`/financial/invoices/${payment.invoice_id}`)}
                  sx={{ mt: 2 }}
                >
                  View Invoice
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No invoice linked to this payment
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Transaction Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Amount Paid:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatCurrency(payment?.amount)}
              </Typography>
            </Box>
            {payment?.fee_amount && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Processing Fee:</Typography>
                  <Typography variant="body1">
                    -{formatCurrency(payment?.fee_amount)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="medium">Net Received:</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency((payment?.amount || 0) - (payment?.fee_amount || 0))}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Log
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Activity history would be displayed here.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentDetail;